import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Gauge, Smartphone, Monitor, RefreshCw, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import TabFreshnessNote from "./TabFreshnessNote";
import { useAuth } from "../../lib/auth";

/**
 * Page Insights tab — Google PageSpeed Insights (Core Web Vitals + Lighthouse
 * scores) for the top 10 GSC landing pages.
 *
 * Data flow: reads from `pagespeed_snapshots` (weekly Tuesday 09:00 UTC cron),
 * with an admin-only "Run scan now" button that forces a fresh sweep. Never
 * triggers PSI from a mount — PSI calls are 25-45s each and would freeze the UI.
 *
 * "No field data available" (CrUX) is explicitly handled per user request —
 * common on long-tail pages that don't yet have enough real-user traffic for
 * Google to report 75th-percentile CWV numbers.
 */

// -----------------------------------------------------------------------------
// Score helpers
// -----------------------------------------------------------------------------

const scoreColor = (n) => {
  if (n == null) return "#a0aec0";
  if (n >= 90) return "#048056";   // green
  if (n >= 50) return "#d97706";   // amber
  return "#c53030";                 // red
};

const cwvColor = (kind, value) => {
  // Google's official pass/fail thresholds (mobile field data)
  if (value == null) return "#a0aec0";
  if (kind === "lcp_ms") return value <= 2500 ? "#048056" : value <= 4000 ? "#d97706" : "#c53030";
  if (kind === "inp_ms") return value <= 200 ? "#048056" : value <= 500 ? "#d97706" : "#c53030";
  if (kind === "cls")     return value <= 0.1 ? "#048056" : value <= 0.25 ? "#d97706" : "#c53030";
  return "#4a5568";
};

const fmtMs = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n} ms`);
const fmtCls = (n) => (n == null ? "—" : n.toFixed(2));
const fmtTimeAgo = (iso) => {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hrs = Math.floor(diff / (1000 * 60 * 60));
  if (days >= 1) return `${days}d ago`;
  if (hrs >= 1) return `${hrs}h ago`;
  return "just now";
};

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function ScorePill({ label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
        style={{ backgroundColor: scoreColor(value) }}
        data-testid={`ps-score-${label.toLowerCase()}`}
      >
        {value == null ? "—" : value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold">{label}</div>
    </div>
  );
}

function CwvCell({ kind, label, value, fmt }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[#a0aec0] font-semibold">{label}</div>
      <div
        className="font-mono-tight text-sm font-semibold mt-0.5"
        style={{ color: cwvColor(kind, value) }}
      >
        {fmt(value)}
      </div>
    </div>
  );
}

function StrategySection({ strategy, data }) {
  const Icon = strategy === "mobile" ? Smartphone : Monitor;
  const label = strategy === "mobile" ? "Mobile" : "Desktop";

  if (!data || data.error) {
    return (
      <div className="border-t border-[#edf2f7] pt-3 mt-3">
        <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
          <Icon className="h-3.5 w-3.5" />
          <span className="font-semibold">{label}</span>
          <span className="ml-2 text-[#c53030]">Scan failed</span>
        </div>
        {data?.error && (
          <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-1">{data.error.slice(0, 120)}</div>
        )}
      </div>
    );
  }

  const scores = data.scores || {};
  const field = data.field_data;
  const originFallback = data.field_origin_fallback;
  const lab = data.lab || {};

  return (
    <div className="border-t border-[#edf2f7] pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-3.5 w-3.5 text-[#4a5568]" />
        <span className="text-xs font-semibold text-[#1a202c]">{label}</span>
      </div>

      {/* Score pills */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
        <ScorePill label="Perf" value={scores.performance} />
        <ScorePill label="A11y" value={scores.accessibility} />
        <ScorePill label="Best Pr." value={scores.best_practices} />
        <ScorePill label="SEO" value={scores.seo} />
      </div>

      {/* Core Web Vitals — field data preferred, lab as fallback */}
      {field ? (
        <>
          <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold mb-2">
            Core Web Vitals · real users (CrUX)
          </div>
          <div className="grid grid-cols-3 gap-x-4">
            <CwvCell kind="lcp_ms" label="LCP" value={field.lcp_ms} fmt={fmtMs} />
            <CwvCell kind="inp_ms" label="INP" value={field.inp_ms} fmt={fmtMs} />
            <CwvCell kind="cls"    label="CLS" value={field.cls}    fmt={fmtCls} />
          </div>
        </>
      ) : (
        <div
          className="rounded-lg border border-dashed border-[#e2e8f0] bg-[#fafafa] px-3 py-2.5 text-[11px] text-[#718096] leading-snug"
          data-testid={`ps-no-field-${strategy}`}
        >
          <div className="font-semibold text-[#4a5568]">No field data available</div>
          <div className="mt-0.5">
            This page doesn&apos;t yet have enough real-user traffic for Google to report 75th-percentile Core Web Vitals.
            {originFallback && " Lab scores above reflect a synthetic Lighthouse run."}
          </div>
        </div>
      )}

      {/* Lab data as small footnote — always present */}
      {field && (
        <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-3">
          Lab · LCP {fmtMs(lab.lcp_ms)} · CLS {fmtCls(lab.cls)} · TBT {fmtMs(lab.tbt_ms)}
        </div>
      )}

      {/* Top opportunities */}
      {(data.opportunities || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-dashed border-[#edf2f7]">
          <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold mb-1.5">
            Top opportunities
          </div>
          <ul className="space-y-1">
            {data.opportunities.map((o) => (
              <li key={o.id} className="text-[11px] text-[#4a5568] flex items-start gap-1.5">
                <span className="font-mono-tight text-[#c53030] shrink-0">−{fmtMs(o.savings_ms)}</span>
                <span className="min-w-0">{o.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PageCard({ result }) {
  const [expanded, setExpanded] = useState(false);
  const url = result.url || "";
  const shortUrl = url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const mobileScore = result.mobile?.scores?.performance ?? null;
  const desktopScore = result.desktop?.scores?.performance ?? null;

  return (
    <div className="card-soft p-5" data-testid={`ps-page-${shortUrl.slice(0, 30)}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="font-mono-tight text-sm text-[#1a202c] hover:text-[#2c7a7b] inline-flex items-center gap-1.5"
          >
            <span className="truncate max-w-md">{shortUrl}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
          </a>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="flex items-center gap-1.5" title={`Mobile performance: ${mobileScore ?? "—"}`}>
            <Smartphone className="h-3 w-3 text-[#a0aec0]" />
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[10.5px] font-bold"
              style={{ backgroundColor: scoreColor(mobileScore) }}
              data-testid={`ps-summary-mobile-${shortUrl.slice(0, 30)}`}
            >
              {mobileScore == null ? "—" : mobileScore}
            </div>
          </div>
          <div className="flex items-center gap-1.5" title={`Desktop performance: ${desktopScore ?? "—"}`}>
            <Monitor className="h-3 w-3 text-[#a0aec0]" />
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[10.5px] font-bold"
              style={{ backgroundColor: scoreColor(desktopScore) }}
              data-testid={`ps-summary-desktop-${shortUrl.slice(0, 30)}`}
            >
              {desktopScore == null ? "—" : desktopScore}
            </div>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] font-semibold text-[#2c7a7b] hover:underline"
            data-testid={`ps-toggle-${shortUrl.slice(0, 30)}`}
          >
            {expanded ? "Hide details" : "Details"}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <StrategySection strategy="mobile" data={result.mobile} />
          <StrategySection strategy="desktop" data={result.desktop} />
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main tab
// -----------------------------------------------------------------------------

export default function PageInsightsTab({ client }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [state, setState] = useState({ loading: true, snapshot: null, configured: null });
  const [running, setRunning] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/clients/${client.id}/pagespeed`);
      setState({ loading: false, snapshot: data.snapshot, configured: data.configured });
    } catch (err) {
      setState({ loading: false, snapshot: null, configured: null });
      toast.error("Failed to load page insights");
    }
  };

  useEffect(() => { load(); }, [client.id]);

  const runScan = async () => {
    if (!window.confirm("Run a fresh PageSpeed sweep? This takes 30-90 seconds while Google renders each page.")) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/pagespeed/run`);
      setState((s) => ({ ...s, snapshot: data.snapshot }));
      toast.success(`Scanned ${data.snapshot?.results?.length || 0} pages.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Scan failed";
      toast.error(typeof msg === "string" ? msg : "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const snap = state.snapshot;
  const results = snap?.results || [];

  return (
    <div className="space-y-5" data-testid="pageinsights-tab">
      <TabFreshnessNote testid="pageinsights-freshness">
        Auto-scanned weekly · Tue 09:00 UTC · Top 10 GSC landing pages (last 28d clicks) · Cached results shown between runs.
      </TabFreshnessNote>

      {/* Header */}
      <div className="card-soft p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold flex items-center gap-1.5">
            <Gauge className="h-3 w-3" /> Page Insights
          </div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Core Web Vitals · top 10 landing pages</h3>
          <div className="text-xs text-[#718096] mt-1">
            {snap
              ? <>Last scan: <span className="font-mono-tight">{fmtTimeAgo(snap.at)}</span> · {results.length} page{results.length === 1 ? "" : "s"}</>
              : "No scans yet."}
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={runScan}
            disabled={running || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="pageinsights-run-scan"
            className="btn-primary inline-flex items-center gap-2"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {running ? "Scanning…" : "Run scan now"}
          </button>
        )}
      </div>

      {/* Not configured warning */}
      {state.configured === false && (
        <div className="card-soft p-4 border-l-4 border-l-[#ed8936] flex items-start gap-2.5" data-testid="pageinsights-not-configured">
          <AlertCircle className="h-4 w-4 text-[#ed8936] shrink-0 mt-0.5" />
          <div className="text-xs text-[#4a5568]">
            <div className="font-semibold text-[#c05621]">PageSpeed Insights API key not configured</div>
            <div className="mt-1">Add <code className="font-mono-tight bg-[#f7fafc] px-1 rounded">PAGESPEED_API_KEY</code> to the backend .env and restart to enable scans.</div>
          </div>
        </div>
      )}

      {/* GSC-not-connected warning */}
      {!client.gsc_property_url && (
        <div className="card-soft p-4 border-l-4 border-l-[#ed8936] flex items-start gap-2.5" data-testid="pageinsights-no-gsc">
          <AlertCircle className="h-4 w-4 text-[#ed8936] shrink-0 mt-0.5" />
          <div className="text-xs text-[#4a5568]">
            <div className="font-semibold text-[#c05621]">Connect Search Console first</div>
            <div className="mt-1">Page Insights scans your top 10 GSC landing pages. Connect Search Console on this client to enable the sweep.</div>
          </div>
        </div>
      )}

      {state.loading && (
        <div className="text-sm text-[#a0aec0] font-mono-tight">Loading page insights…</div>
      )}

      {!state.loading && !snap && client.gsc_property_url && state.configured !== false && (
        <div className="card-soft p-6 text-center text-sm text-[#718096]" data-testid="pageinsights-empty">
          No scans yet. The weekly cron runs Tuesday 09:00 UTC — or click <span className="font-semibold">Run scan now</span> to trigger one immediately.
        </div>
      )}

      {!state.loading && results.length > 0 && (
        <div className="space-y-2.5">
          {results.map((r, i) => (
            <PageCard key={i} result={r} />
          ))}
        </div>
      )}
    </div>
  );
}
